import React, { useEffect } from 'react';
import {
	SpaceBetween,
	Container,
	Header,
	KeyValuePairs,
	Spinner,
	Box,
} from '@amzn/awsui-components-console';

import { useFetchForSDS } from 'src/hooks/useFetchForSDS';
import { GetApplicationResponse } from '@amzn/snow-data-service-typescript-client';
import OperationList from '../../OperationManagement/OperationList/OperationList';
import { SDS_APIGATEWAY_ENDPOINT } from 'src/config';
import { getApplicationDetailsItems } from './ApplicationDetailsConstants';
import { useNavigation } from '../../navigation/hooks/useNavigation';
import { ROUTES } from '../../navigation/types/navigationTypes';
import { useStore } from 'src/stores/store';

interface ApplicationDetailsWithOperationsProps {
	applicationId: string;
	operationListRef: React.MutableRefObject<{ fetchData: () => void } | null>;
}

const ApplicationDetailsWithOperations: React.FC<
	ApplicationDetailsWithOperationsProps
> = ({ applicationId, operationListRef }) => {
	const { navigate } = useNavigation();
	const { addNavigationError } = useStore();

	const { isLoading, data, error } =
		useFetchForSDS<GetApplicationResponse>({
			url: SDS_APIGATEWAY_ENDPOINT,
			path: applicationId ? `/application/${applicationId}` : '',
			method: 'GET',
		});

	useEffect(() => {
		if (!applicationId) {
			addNavigationError({
				message: 'No application selected. Please select an application from the list.',
				type: 'warning',
				timestamp: Date.now(),
			});
			navigate(ROUTES.APPLICATIONS, '/applicationManagement');
		}
	}, [applicationId, addNavigationError, navigate]);

	useEffect(() => {
		if (error) {
			addNavigationError({
				message: `Failed to load application details: ${error}`,
				type: 'error',
				timestamp: Date.now(),
			});
		}
	}, [error, addNavigationError]);

	const applicationData = data?.applicationProfileMetadata;
	const { applicationInfoItems, classificationItems } = applicationData
		? getApplicationDetailsItems(applicationData)
		: { applicationInfoItems: [], classificationItems: [] };

	if (!applicationId) {
		return null;
	}

	return isLoading ? (
		<Box textAlign="center" padding="l">
			<Spinner size="large" />
		</Box>
	) : error ? null : (
		<SpaceBetween size="l">
			<Container
				header={<Header variant="h2">Application Information</Header>}
			>
				<KeyValuePairs columns={2} items={applicationInfoItems} />
			</Container>

			{classificationItems.length > 0 && (
				<Container
					header={<Header variant="h2">Ownership Information</Header>}
				>
					<KeyValuePairs columns={3} items={classificationItems} />
				</Container>
			)}

			<OperationList
				selectedApplicationId={applicationId}
				ref={operationListRef}
			/>
		</SpaceBetween>
	);
};

export default ApplicationDetailsWithOperations;
